import * as React from "react";
import { makeStyles, useMediaQuery, useTheme } from "@material-ui/core";
import StoreKeys from "common/storeKeys";
import { connect } from "common/connect";
import { EventStageTypes } from "common/constants/EventStageTypes";
import PicksHelper from "common/picksHelper";
import EventHeader from "common/components/EventHeader/EventHeader";
import EventFooter from "common/components/EventFooter/EventFooter";
import Store from "common/store";
import { useForceUpdate } from "common/helpers/componentHelper";
import BracketRenderer from "./Brackets/BracketRenderer";
import BountyBracketRenderer from "./Brackets/BountyBracketRenderer";
import SwissStageRenderer from "./SwissStage/SwissStageRenderer";
import GroupStageRenderer from "./GroupStage/GroupStageRenderer";
import EventOverview from "../EventOverview/EventOverview";
import ChampionReminder from "common/components/ChampionReminder/ChampionReminder";
// eslint-disable-next-line no-unused-vars
import { EventFormatData, getOpenStageIndices } from "common/helpers/eventFormatHelper";
import StageSelector from "./StageSelector";

const useStyles = makeStyles(() => {
	return {
		fitColumn: {
			display: "flex",
			flexDirection: "column",
			height: "100%",
		},
	};
});

const propKeys = {
	eventData: StoreKeys.EVENT.EVENTDATA,
	stageIndex: StoreKeys.EVENT.STAGE_INDEX,
	format: StoreKeys.EVENT.FORMAT,
	isEditing: StoreKeys.EVENT.IS_EDITING,
	showingOfficial: StoreKeys.EVENT.SHOW_OFFICIAL,
	view: StoreKeys.EVENT.VIEW,
};

/**
 * @typedef EventRendererCProps
 * @type {object}
 * @property {any} eventData
 * @property {number} stageIndex
 * @property {EventFormatData} format
 * @property {boolean} isEditing
 * @property {boolean} isAdmin
 * @property {any} adminEventData
 */

/**
 * @param {EventRendererCProps} props
 */
function EventRendererC({
	eventData,
	stageIndex,
	format,
	isEditing,
	showingOfficial,
	view,
	isAdmin,
	disableOverview,
}) {
	const forceUpdate = useForceUpdate();
	const styles = useStyles();
	const theme = useTheme();
	const isSmall = useMediaQuery(theme.breakpoints.down("sm"));

	React.useEffect(() => {
		const callback = () => {
			forceUpdate();
		};
		window.addEventListener("resize", callback);

		return () => {
			window.removeEventListener("resize", callback);
		};
	});

	if (!eventData || !format) {
		return <></>;
	}

	const picksHelper = new PicksHelper(eventData);
	const isStageOpen = getOpenStageIndices(eventData, format).includes(stageIndex);
	const isLocked = !isAdmin && (!isStageOpen || picksHelper.isLocked());

	const onEdit = () => {
		if (isLocked) {
			return;
		}

		const updates = {};
		updates[StoreKeys.EVENT.IS_EDITING] = true;
		updates[StoreKeys.EVENT.STEP] = 0;
		Store.setMany(updates);
	};

	const onShowOfficialResults = (value) => {
		Store.set(
			StoreKeys.EVENT.SHOW_OFFICIAL,
			value ?? !Store.get(StoreKeys.EVENT.SHOW_OFFICIAL)
		);
	};

	const currentStage = format.stages[stageIndex];
	const renderWidth = !isSmall
		? window.innerWidth - 382
		: window.innerWidth - 24;

	const isBracketStage =
		currentStage?.type === EventStageTypes.Playoffs ||
		(currentStage?.type === EventStageTypes.Group &&
			currentStage?.groupStageDetails?.hasGroupBrackets);

	const isSwissStage = currentStage?.type === EventStageTypes.Swiss;

	const isGroupStage =
		currentStage?.type === EventStageTypes.Group &&
		!currentStage?.groupStageDetails?.hasGroupBrackets;

	const isBountyStage = currentStage?.type === EventStageTypes.Bounty;

	const hasMultipleStages = format.stages.length > 0;

	const championEnabled = !disableOverview && !!format.championPick?.enabled;
	const showOverview = championEnabled && view === "overview";

	// Only brackets can shrink, so only they can guarantee the buttons stay on screen.
	const fitBracket = !showOverview && (isBracketStage || isBountyStage);

	return (
		<div className={fitBracket ? styles.fitColumn : undefined}>
			{!showOverview && (isLocked || !isEditing) && (
				<EventHeader width={renderWidth} />
			)}
			{championEnabled && <ChampionReminder />}
			{hasMultipleStages && (!isEditing || championEnabled) && (
				<StageSelector disableOverview={disableOverview} />
			)}
			{showOverview ? (
				<EventOverview width={renderWidth} />
			) : (
				<>
					{isBracketStage && <BracketRenderer />}
					{isBountyStage && <BountyBracketRenderer />}
					{isSwissStage && <SwissStageRenderer width={renderWidth} />}
					{isGroupStage && <GroupStageRenderer width={renderWidth} />}
					{(isLocked || !isEditing) && (
						<EventFooter
							width={renderWidth}
							onEdit={onEdit}
							showingOfficialResults={showingOfficial}
							showOfficialResults={onShowOfficialResults}
							picksLocked={isLocked}
							isStageOpen={isStageOpen}
						/>
					)}
				</>
			)}
		</div>
	);
}

export default function EventRenderer(props) {
	return connect(<EventRendererC />, propKeys, props);
}
